/**
 * build_flow tool - Wizard-style flow builder with simple inputs
 *
 * Takes a name and goal description, handles all complexity internally,
 * and returns clear status updates.
 */

import { z } from "zod";
import type { FlowManagementApi } from "../api/flow-management.js";
import type { ConnectorMetadataApi } from "../api/connector-metadata.js";
import type { CreateFlowRequest, FlowDefinition, ConnectionReference, ActionRunAfterStatus } from "../api/types.js";
import { validateFlow } from "../validator/index.js";
import { logger } from "../utils/logger.js";
import { parseSchedulePhrase, recurrenceConfig, detectConnectorMentions, CONNECTOR_API_IDS, type ParsedSchedule } from "./plan-flow.js";
import { extractArmErrorMessage, ERROR_DETAIL_LIMIT } from "../utils/format.js";

export const buildFlowInputSchema = z.object({
  name: z.string().describe("Name for the flow"),
  goal: z.string().describe("What the flow should do (plain English)"),
  dryRun: z.boolean().optional().describe("If true, just show what would be created without creating it"),
});

export type BuildFlowInput = z.infer<typeof buildFlowInputSchema>;

export const buildFlowTool = {
  name: "build_flow",
  description: `Build a Power Automate flow from a description.

RECOMMENDED: Use plan_flow FIRST to gather requirements through the interactive wizard, then use this tool to create the flow.

This tool:
1. Analyzes the goal
2. Detects required connections
3. Creates the flow directly

Missing connections do NOT block creation: the flow is built anyway (stopped, so it can't fail-run), and the response lists exactly which connections still need to be configured at make.powerautomate.com. Connections that already exist are used automatically.

For complex flows with multiple data sources, approvals, or specific timing requirements, use plan_flow first to clarify details.`,
  inputSchema: {
    type: "object" as const,
    properties: {
      name: {
        type: "string",
        description: "Name for the flow",
      },
      goal: {
        type: "string",
        description: "What the flow should do (plain English). Include any details gathered from plan_flow.",
      },
      dryRun: {
        type: "boolean",
        description: "If true, just show what would be created without creating it",
      },
    },
    required: ["name", "goal"],
  },
};

export interface BuildFlowContext {
  flowApi: FlowManagementApi;
  connectorApi: ConnectorMetadataApi | null;
}

interface FlowPlan {
  trigger: {
    type: string;
    connector?: string;
    description: string;
  };
  actions: Array<{
    name: string;
    type: string;
    connector?: string;
    description: string;
    parameters?: Record<string, unknown>;
  }>;
  requiredConnections: string[];
  /** Recurrence parsed from the goal, when it names one. */
  schedule?: ParsedSchedule;
  /** Services the goal names that this builder cannot author actions for. */
  unsupported?: string[];
}

export async function handleBuildFlow(
  context: BuildFlowContext,
  input: unknown
): Promise<string> {
  const parsed = buildFlowInputSchema.parse(input);

  logger.info({ name: parsed.name, goal: parsed.goal }, "Building flow");

  // Analyze the goal and create a plan
  const plan = analyzeGoal(parsed.goal);

  // Check for required connections and get their IDs.
  // Three states per required connector:
  //   connected — healthy connection found, bound silently
  //   broken    — a connection exists but isn't in Connected status; bound anyway
  //               (the reference resolves) and reported so the user can repair it
  //   missing   — no connection at all; the flow is still created with a
  //               placeholder reference and left stopped, so the user gets a
  //               finished flow that only needs the connection configured
  const connectionMap: Map<string, string> = new Map();
  const brokenConnections: Map<string, string> = new Map(); // apiId → status
  const missingConnections: string[] = [];

  if (plan.requiredConnections.length > 0 && context.connectorApi) {
    try {
      const connections = await context.connectorApi.listConnections();
      const fallbackMap: Map<string, { name: string; status: string }> = new Map();
      for (const conn of connections) {
        const apiId = conn.properties.apiId?.split("/").pop() || "";
        if (!apiId) continue;
        const status = conn.properties.statuses?.[0]?.status ?? "Unknown";
        if (status === "Connected") {
          if (!connectionMap.has(apiId)) connectionMap.set(apiId, conn.name);
        } else if (!fallbackMap.has(apiId)) {
          fallbackMap.set(apiId, { name: conn.name, status });
        }
      }
      for (const required of plan.requiredConnections) {
        if (connectionMap.has(required)) continue;
        const fallback = fallbackMap.get(required);
        if (fallback) {
          // Exists but needs repair — bind it and flag it
          connectionMap.set(required, fallback.name);
          brokenConnections.set(required, fallback.status);
        } else {
          missingConnections.push(required);
        }
      }
    } catch {
      missingConnections.push(...plan.requiredConnections);
    }
  }

  const connectionSetupSection = (): string => {
    let s = "";
    if (missingConnections.length > 0) {
      s += `⚠️ **Connections not configured yet:**\n`;
      missingConnections.forEach(c => {
        s += `  • ${getConnectorFriendlyName(c)} — create it at make.powerautomate.com > Data > Connections, then open the flow in the designer and select it\n`;
      });
    }
    if (brokenConnections.size > 0) {
      s += `⚠️ **Existing connections that need repair:**\n`;
      brokenConnections.forEach((status, c) => {
        s += `  • ${getConnectorFriendlyName(c)} (status: ${status}) — fix it at make.powerautomate.com > Data > Connections\n`;
      });
    }
    return s;
  };

  if (parsed.dryRun) {
    const { definition, connectionRefs } = buildFlowDefinition(plan, parsed.name, connectionMap);

    // Validate the definition
    const validation = validateFlow(definition, connectionRefs);
    const issues: string[] = [];

    if (!validation.valid) {
      validation.schemaValidation.errors.forEach(e => issues.push(`Schema: ${e.message}`));
      validation.expressionValidation.errors.filter(e => e.severity === "error").forEach(e => issues.push(`Expression: ${e.message}`));
      validation.connectionValidation.errors.filter(e => e.severity === "error").forEach(e => issues.push(`Connection: ${e.message}`));
    }

    let output = `## Dry Run: "${parsed.name}"\n\n`;
    output += `**Trigger:** ${plan.trigger.description}\n`;
    output += `**Actions:** ${plan.actions.map(a => a.description).join(" → ")}\n`;
    output += `**Connections:** ${plan.requiredConnections.map(c => getConnectorFriendlyName(c)).join(", ") || "None"}\n\n`;
    output += unsupportedSection(plan);

    const setupSection = connectionSetupSection();
    if (setupSection) {
      output += `${setupSection}\nThe flow can still be created — it will be left stopped until the connections above are ready.\n\n`;
    }

    if (issues.length > 0) {
      output += `⚠️ **Validation Issues:**\n`;
      issues.forEach(i => output += `  • ${i}\n`);
      output += `\n`;
    } else {
      output += `✅ **Validation passed**\n\n`;
    }

    output += `**Flow Definition Preview:**\n\`\`\`json\n${JSON.stringify(definition, null, 2).slice(0, 1000)}...\n\`\`\`\n`;
    output += `\n_Set dryRun: false to create this flow._`;

    return output;
  }

  try {
    const { definition, connectionRefs } = buildFlowDefinition(plan, parsed.name, connectionMap);

    // Validate before creating
    const validation = validateFlow(definition, connectionRefs);
    if (!validation.valid) {
      const issues: string[] = [];
      validation.schemaValidation.errors.forEach(e => issues.push(`Schema: ${e.path} - ${e.message}`));
      validation.expressionValidation.errors.filter(e => e.severity === "error").forEach(e => issues.push(`Expression: ${e.message}`));
      validation.connectionValidation.errors.filter(e => e.severity === "error").forEach(e => issues.push(`Connection: ${e.message}`));

      return `Flow validation failed:\n\n${issues.map(i => `• ${i}`).join("\n")}\n\nFix these issues and try again.`;
    }

    const flowRequest: CreateFlowRequest = {
      properties: {
        displayName: parsed.name,
        definition,
        connectionReferences: Object.keys(connectionRefs).length > 0 ? connectionRefs : undefined,
      },
    };

    const flow = await context.flowApi.createFlow(flowRequest);

    const needsConnectionWork = missingConnections.length > 0 || brokenConnections.size > 0;

    // A flow with unconfigured connections is created Started by the API and
    // would fail on every trigger — stop it so the user configures first.
    let stoppedForSetup = false;
    if (needsConnectionWork) {
      try {
        await context.flowApi.stopFlow(flow.name);
        stoppedForSetup = true;
      } catch {
        // Best-effort — the connection warning below still tells the user
      }
    }

    // Suggest next steps
    let result = `✅ Created "${flow.properties.displayName}" (${flow.name})\n\n`;
    result += `**Status:** ${stoppedForSetup ? "Stopped (waiting on connections)" : flow.properties.state}\n`;
    result += unsupportedSection(plan);

    if (needsConnectionWork) {
      result += `\n${connectionSetupSection()}\n`;
      result += `**Next steps:**\n`;
      result += `1. Configure the connection(s) above at make.powerautomate.com > Data > Connections\n`;
      result += `2. Open the flow in the designer and assign the new connection(s) to its steps\n`;
      result += `3. Start the flow: toggle_flow with flowId "${flow.name}" and action "start"\n`;
      result += `4. Test it: run_flow with flowId "${flow.name}"\n`;
    } else {
      result += `**Next steps:**\n`;
      result += `1. Test the flow: run_flow with flowId "${flow.name}"\n`;
      result += `2. If it fails: diagnose_flow with flowId "${flow.name}"\n`;
      result += `3. To modify: update_flow with flowId "${flow.name}"\n`;
    }

    return result;

  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    let details = "";
    if (error && typeof error === "object" && "details" in error) {
      const errDetails = (error).details;
      details = typeof errDetails === "string" ? errDetails : JSON.stringify(errDetails);
    }

    const fullMsg = msg + " " + details;
    logger.error({ error, details }, "Flow creation failed");

    if (fullMsg.includes("ConnectionAuthorizationFailed")) {
      if (missingConnections.length > 0 || brokenConnections.size > 0) {
        // This environment refuses to create flows with unconfigured
        // connections — fall back to telling the user exactly what to set up.
        let result = `The flow is fully designed, but this environment requires its connections to exist before the flow can be created.\n\n`;
        result += `${connectionSetupSection()}\n`;
        result += `Once the connection(s) exist, run this same build_flow request again — it will pick them up automatically and create the flow.`;
        return result;
      }
      const connMatch = fullMsg.match(/apis\/([^/]+)/);
      const connName = connMatch?.[1] ? getConnectorFriendlyName(connMatch[1]) : "a connector";
      return `Missing ${connName} connection. Create it at make.powerautomate.com > Data > Connections.`;
    }

    if (fullMsg.includes("DynamicParameterInputInvalid")) {
      // The scaffolded connector action is missing required inputs the API
      // validates at save time (e.g. Excel needs a workbook source). build_flow
      // can't infer those from a plain-English goal.
      const detailMatch = fullMsg.match(/Error details: (.+?)\.'/);
      let result = `The flow design needs action parameters that can't be inferred from the goal alone`;
      result += detailMatch?.[1] ? ` (${detailMatch[1].trim()}).` : ".";
      result += `\n\nUse plan_flow to gather the specifics (which file, site, channel, etc.), then create_flow with explicit action parameters.`;
      return result;
    }

    const extracted = extractArmErrorMessage(fullMsg);
    return `Failed: ${extracted ? extracted.slice(0, ERROR_DETAIL_LIMIT) : msg.slice(0, ERROR_DETAIL_LIMIT)}`;
  }
}

/** The goal named services this builder cannot author; say so instead of substituting a Compose. */
function unsupportedSection(plan: FlowPlan): string {
  if (!plan.unsupported || plan.unsupported.length === 0) return "";
  const names = plan.unsupported.join(", ");
  return (
    `⚠️ **Not authored here:** ${names}. build_flow has no action template for ${plan.unsupported.length > 1 ? "these services" : "this service"}, so the flow does not include ${plan.unsupported.length > 1 ? "them" : "it"}. ` +
    `Add the step in the designer, or use get_action_schema on the connector and create_flow / update_flow with the exact operation and parameters.\n\n`
  );
}

/**
 * Analyze the goal and determine what's needed
 */
function analyzeGoal(goal: string): FlowPlan {
  const lower = goal.toLowerCase();
  const plan: FlowPlan = {
    trigger: { type: "Request", description: "Manual trigger (button)" },
    actions: [],
    requiredConnections: [],
  };

  // Detect trigger type. A schedule phrase ("every Monday at 8am", "every 5
  // minutes") becomes the exact Recurrence; the old check knew only "daily".
  const schedule = parseSchedulePhrase(goal);
  if (schedule) {
    plan.trigger = { type: "Recurrence", description: `Scheduled: ${schedule.label}` };
    plan.schedule = schedule;
  } else if (lower.includes("every day") || lower.includes("daily") || lower.includes("schedule")) {
    plan.trigger = { type: "Recurrence", description: "Scheduled (daily)" };
  } else if (lower.includes("every hour") || lower.includes("hourly")) {
    plan.trigger = { type: "Recurrence", description: "Scheduled (hourly)" };
  } else if (lower.includes("when email") || lower.includes("new email")) {
    plan.trigger = { type: "OpenApiConnection", connector: "shared_office365", description: "When new email arrives" };
    plan.requiredConnections.push("shared_office365");
  } else if (lower.includes("when file") || lower.includes("new file") || lower.includes("sharepoint")) {
    plan.trigger = { type: "OpenApiConnection", connector: "shared_sharepointonline", description: "When file created/modified" };
    plan.requiredConnections.push("shared_sharepointonline");
  }

  // Detect Excel operations
  if (lower.includes("excel") || lower.includes("spreadsheet") || lower.includes("xlsx")) {
    plan.actions.push({
      name: "Excel_Operation",
      type: "OpenApiConnection",
      connector: "shared_excelonlinebusiness",
      description: "Read/update Excel file",
    });
    plan.requiredConnections.push("shared_excelonlinebusiness");
  }

  // Detect email operations — "email a summary to x@y" is a send even without
  // the verb "send"; an address in the goal becomes the recipient.
  const address = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i.exec(goal)?.[0];
  const emailVerb = /\b(send|notify|summary|report|digest|alert|remind|mail)\b/.test(lower);
  if (lower.includes("send email") || (lower.includes("email") && (lower.includes("send") || emailVerb || address))) {
    plan.actions.push({
      name: "Send_Email",
      type: "OpenApiConnection",
      connector: "shared_office365",
      description: address ? `Send email to ${address}` : "Send email",
      ...(address ? { parameters: { "emailMessage/To": address } } : {}),
    });
    if (!plan.requiredConnections.includes("shared_office365")) {
      plan.requiredConnections.push("shared_office365");
    }
  }

  // Detect Graph API / Entra / License checks
  if (lower.includes("license") || lower.includes("entra") || lower.includes("azure ad") || lower.includes("graph api")) {
    plan.actions.push({
      name: "Check_Licenses",
      type: "OpenApiConnection",
      connector: "shared_webcontentsv2",
      description: "Call Microsoft Graph API (requires Premium)",
    });
    plan.requiredConnections.push("shared_webcontentsv2");
  }

  // Detect Teams operations
  if (lower.includes("teams") && (lower.includes("post") || lower.includes("message") || lower.includes("notify"))) {
    plan.actions.push({
      name: "Teams_Message",
      type: "OpenApiConnection",
      connector: "shared_teams",
      description: "Post to Teams",
    });
    plan.requiredConnections.push("shared_teams");
  }

  // Detect approval workflows
  if (lower.includes("approv")) {
    plan.actions.push({
      name: "Start_Approval",
      type: "OpenApiConnection",
      connector: "shared_approvals",
      description: "Start approval process",
    });
    plan.requiredConnections.push("shared_approvals");
  }

  // Detect HTTP/API calls
  if (lower.includes("api") || lower.includes("http") || lower.includes("rest") || lower.includes("webhook")) {
    if (!plan.requiredConnections.includes("shared_webcontentsv2")) {
      plan.actions.push({
        name: "HTTP_Request",
        type: "Http",
        description: "Make HTTP request",
      });
    }
  }

  // Services the goal names but this builder has no action template for.
  // Say so rather than silently emitting a Compose in their place.
  const authorable = new Set(["shared_office365", "shared_sharepointonline", "shared_excelonlinebusiness", "shared_teams", "shared_approvals", "shared_webcontentsv2"]);
  const unsupported = detectConnectorMentions(goal).filter((c) => !authorable.has(CONNECTOR_API_IDS[c] ?? ""));
  if (unsupported.length > 0) plan.unsupported = unsupported;

  // Default action if none detected
  if (plan.actions.length === 0) {
    plan.actions.push({
      name: "Compose",
      type: "Compose",
      description: "Process data",
    });
  }

  return plan;
}

/**
 * Build the actual flow definition from the plan
 */
function buildFlowDefinition(
  plan: FlowPlan,
  _name: string,
  connectionMap: Map<string, string>
): {
  definition: FlowDefinition;
  connectionRefs: Record<string, ConnectionReference>;
} {
  const definition: FlowDefinition = {
    $schema: "https://schema.management.azure.com/providers/Microsoft.Logic/schemas/2016-06-01/workflowdefinition.json#",
    contentVersion: "1.0.0.0",
    triggers: {},
    actions: {},
  };

  const connectionRefs: Record<string, ConnectionReference> = {};

  // Build trigger
  if (plan.trigger.type === "Request") {
    definition.triggers["manual"] = {
      type: "Request",
      kind: "Button",
      inputs: { schema: {} },
    };
  } else if (plan.trigger.type === "Recurrence") {
    definition.triggers["Recurrence"] = (plan.schedule
      ? recurrenceConfig(plan.schedule)
      : {
          type: "Recurrence",
          recurrence: {
            frequency: "Day",
            interval: 1,
          },
        }) as unknown as FlowDefinition["triggers"][string];
  } else if (plan.trigger.connector) {
    const connectorKey = plan.trigger.connector;
    const actualConnId = connectionMap.get(connectorKey) || connectorKey;
    definition.triggers["trigger"] = {
      type: "OpenApiConnection",
      inputs: {
        host: {
          connectionName: connectorKey,  // References the key in connectionReferences
          operationId: "OnNewEmail",
          apiId: `/providers/Microsoft.PowerApps/apis/${connectorKey}`,
        },
      },
      // Polling triggers are rejected by the flows API without a recurrence
      // ("The 'recurrence' property of template trigger ... is not defined") —
      // 3 minutes matches the designer's default polling interval.
      recurrence: {
        frequency: "Minute",
        interval: 3,
      },
    };
    connectionRefs[connectorKey] = {
      connectionName: actualConnId,  // The actual connection GUID
      id: `/providers/Microsoft.PowerApps/apis/${connectorKey}`,
      source: "Embedded",
      tier: "NotSpecified",
    };
  }

  // Build actions
  let previousAction: string | null = null;

  for (const action of plan.actions) {
    const runAfter: Record<string, ActionRunAfterStatus[]> = previousAction
      ? { [previousAction]: ["Succeeded"] }
      : {};

    if (action.type === "Compose") {
      definition.actions[action.name] = {
        type: "Compose",
        inputs: { value: "@{triggerBody()}" },
        runAfter,
      };
    } else if (action.type === "Http") {
      definition.actions[action.name] = {
        type: "Http",
        inputs: {
          method: "GET",
          uri: "https://api.example.com",
        },
        runAfter,
      };
    } else if (action.connector) {
      // Build actual connector action with proper structure
      const connectorKey = action.connector;
      const actualConnId = connectionMap.get(connectorKey) || connectorKey;
      const operationId = getDefaultOperationId(connectorKey, action.name);

      definition.actions[action.name] = {
        type: "OpenApiConnection",
        inputs: {
          host: {
            connectionName: connectorKey,
            operationId: operationId,
            apiId: `/providers/Microsoft.PowerApps/apis/${connectorKey}`,
          },
          parameters: action.parameters || {},
        },
        runAfter,
      };

      // Add connection reference
      connectionRefs[connectorKey] = {
        connectionName: actualConnId,
        id: `/providers/Microsoft.PowerApps/apis/${connectorKey}`,
        source: "Embedded",
        tier: "NotSpecified",
      };
    }

    previousAction = action.name;
  }

  // Add connection parameters if we have connections
  if (Object.keys(connectionRefs).length > 0) {
    definition.parameters = {
      $connections: { defaultValue: {}, type: "Object" },
      $authentication: { defaultValue: {}, type: "SecureObject" },
    };
  }

  return { definition, connectionRefs };
}

/**
 * Get friendly connector name
 */
function getConnectorFriendlyName(connectorId: string): string {
  const names: Record<string, string> = {
    "shared_excelonlinebusiness": "Excel Online (Business)",
    "shared_office365": "Office 365 Outlook",
    "shared_sharepointonline": "SharePoint",
    "shared_teams": "Microsoft Teams",
    "shared_webcontentsv2": "HTTP with Microsoft Entra ID (Premium)",
    "shared_approvals": "Approvals",
    "shared_office365users": "Office 365 Users",
  };
  return names[connectorId] || connectorId.replace("shared_", "").replace(/_/g, " ");
}

/**
 * Get default operation ID for a connector
 */
function getDefaultOperationId(connector: string, _actionName: string): string {
  const defaults: Record<string, string> = {
    "shared_excelonlinebusiness": "GetItems",
    "shared_office365": "SendEmailV2",
    "shared_sharepointonline": "GetItems",
    "shared_teams": "PostMessageToChannel",
    "shared_webcontentsv2": "InvokeHttp",
    "shared_approvals": "StartAndWaitForAnApproval",
  };
  return defaults[connector] || "Execute";
}
